import React, { useEffect, useRef, useState } from 'react';
import { Autocomplete, Box, Chip, IconButton, Menu, MenuItem, Stack, TextField, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { launchApi } from '../api';
import { launchColors } from '../launchTheme';
import { YearbookProductMatch, YearbookProductOption, YearbookRow } from '../types';
import AssetHoverLink, { assetThumbnailUrl, openAssetInAthena } from '../ui/AssetHoverLink';
import { openProductInfo } from '../productInfo/productInfoLink';
import { openAlcoholInfo } from '../alcoholInfo/alcoholInfoLink';
import { cellTextSx } from './EditableCell';

export interface ArticlePatch {
  articleNumber: string;
  assetId?: string;
  articleName?: string;
}

interface Props {
  row: YearbookRow;
  match?: YearbookProductMatch;
  editable: boolean;
  canOpenProductInfo?: boolean;
  canOpenAlcoholInfo?: boolean;
  onSave: (patch: ArticlePatch) => Promise<boolean>;
}

const optionKey = (option: YearbookProductOption) => option.assetId || `pi:${option.productId || ''}`;

const Thumb: React.FC<{ option: YearbookProductOption }> = ({ option }) => (
  option.hasThumbnail && option.assetId ? (
    <Box component="img" src={assetThumbnailUrl(option.assetId)} alt="" sx={{ width: 36, height: 36, objectFit: 'contain', borderRadius: '4px', bgcolor: launchColors.section, flexShrink: 0 }} />
  ) : (
    <Box sx={{ width: 36, height: 36, borderRadius: '4px', bgcolor: launchColors.section, flexShrink: 0 }} />
  )
);

const OptionText: React.FC<{ option: YearbookProductOption }> = ({ option }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ ...cellTextSx, fontWeight: 600 }}>{option.title.trim() || 'Utan titel'}</Typography>
    {(option.articleNumber || option.productId) && (
      <Typography sx={{ fontSize: 12, color: launchColors.textSecondary }}>
        {[option.articleNumber, option.productId && !option.assetId ? (option.source === 'alcohol' ? 'Produktinfo Alkohol' : 'Produktinformation') : ''].filter(Boolean).join(' · ')}
      </Typography>
    )}
  </Box>
);

export const normalizeArticle = (value: string): string => {
  const compact = value.replace(/\s+/g, '').toUpperCase();
  const match = compact.match(/^([A-Z]*)0*(\d+)$/);
  return match ? `${match[1]}${match[2]}` : compact;
};

const exactOptions = (found: YearbookProductOption[], number: string): YearbookProductOption[] => {
  const key = normalizeArticle(number);
  if (!key) return [];
  return found.filter((option) => normalizeArticle(option.articleNumber) === key && option.title.trim());
};

const ArticleNumberCell: React.FC<Props> = ({ row, match, editable, canOpenProductInfo = false, canOpenAlcoholInfo = false, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(row.articleNumber);
  const [options, setOptions] = useState<YearbookProductOption[]>([]);
  const [searchFailed, setSearchFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [menu, setMenu] = useState<HTMLElement | null>(null);
  const done = useRef(false);

  useEffect(() => {
    if (!editing) return undefined;
    const query = draft.trim();
    if (query.length < 3) {
      setOptions([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      launchApi.searchYearbookProducts(query)
        .then((found) => {
          if (cancelled) return;
          setOptions(found);
          setSearchFailed(false);
        })
        .catch(() => {
          if (cancelled) return;
          setOptions([]);
          setSearchFailed(true);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [draft, editing]);

  const start = () => {
    if (!editable || saving) return;
    done.current = false;
    setDraft(row.articleNumber);
    setOptions([]);
    setEditing(true);
  };

  const save = async (patch: ArticlePatch) => {
    setSaving(true);
    try {
      await onSave(patch);
    } finally {
      setSaving(false);
    }
  };

  const saveNumber = async (next: string) => {
    if (next.length < 3) {
      await save({ articleNumber: next });
      return;
    }
    let found = exactOptions(options, next);
    if (found.length !== 1) {
      try {
        found = exactOptions(await launchApi.searchYearbookProducts(next), next);
      } catch {
        found = [];
      }
    }
    const athena = found.filter((option) => option.assetId);
    const option = athena.length === 1 ? athena[0] : athena.length === 0 && found.length === 1 ? found[0] : null;
    if (option) {
      await save({
        articleNumber: option.articleNumber || next,
        ...(option.assetId ? { assetId: option.assetId } : {}),
        articleName: option.title.trim(),
      });
      return;
    }
    await save({ articleNumber: next });
  };

  const commitText = (text: string) => {
    if (done.current) return;
    done.current = true;
    setEditing(false);
    const next = text.trim();
    if (next === row.articleNumber.trim()) return;
    void saveNumber(next);
  };

  const commitOption = (option: YearbookProductOption) => {
    if (done.current) return;
    done.current = true;
    setEditing(false);
    const title = option.title.trim();
    void save({
      articleNumber: option.articleNumber || draft.trim(),
      ...(option.assetId ? { assetId: option.assetId } : {}),
      ...(title ? { articleName: title } : {}),
    });
  };

  const cancel = () => {
    done.current = true;
    setEditing(false);
  };

  if (editing) {
    return (
      <Autocomplete<YearbookProductOption, false, false, true>
        freeSolo
        openOnFocus
        options={options}
        filterOptions={(items) => items}
        inputValue={draft}
        onInputChange={(_event, value, reason) => { if (reason === 'input') setDraft(value); }}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.articleNumber || option.title)}
        isOptionEqualToValue={(option, value) => optionKey(option) === optionKey(value)}
        onChange={(_event, value) => {
          if (typeof value === 'string') commitText(value);
          else if (value) commitOption(value);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            (event as unknown as { defaultMuiPrevented?: boolean }).defaultMuiPrevented = true;
            event.stopPropagation();
            cancel();
          } else if (event.key === 'Tab') {
            commitText(draft);
          }
        }}
        onBlur={() => commitText(draft)}
        renderOption={(props, option) => {
          const { key: _key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key?: string };
          return (
            <li {...rest} key={optionKey(option)}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                <Thumb option={option} />
                <OptionText option={option} />
              </Stack>
            </li>
          );
        }}
        slotProps={{ paper: { sx: { minWidth: 320 } } }}
        renderInput={(params) => (
          <TextField
            {...params}
            autoFocus
            variant="standard"
            placeholder="Skriv artikelnummer"
            helperText={searchFailed ? 'Sökningen misslyckades.' : undefined}
            inputProps={{ ...params.inputProps, 'aria-label': 'Artikelnummer', maxLength: 500 }}
            sx={{ '& .MuiInputBase-input': { fontSize: 13, py: 0.25 } }}
          />
        )}
      />
    );
  }

  const linked = match && (match.status === 'linked' || match.status === 'matched') ? match : null;
  const catalog = match && match.status === 'catalog' ? match : null;
  const ambiguous = match && match.status === 'ambiguous' ? match : null;
  const number = saving ? draft : row.articleNumber;
  const fromAlcohol = catalog?.source === 'alcohol';
  const catalogName = fromAlcohol ? 'Produktinfo Alkohol' : 'Produktinformation Dabas';
  const canOpenCatalog = fromAlcohol ? canOpenAlcoholInfo : canOpenProductInfo;
  const origin = catalog ? `Från ${catalogName}` : !linked && !ambiguous && number ? 'Skrivet för hand' : undefined;

  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
      {linked && row.articleNumber ? (
        <Box sx={{ ...cellTextSx, minWidth: 0 }}>
          <AssetHoverLink assetId={linked.assetId} title={linked.title.trim() || row.articleName} hasThumbnail={linked.hasThumbnail}>
            {row.articleNumber}
          </AssetHoverLink>
        </Box>
      ) : (
        <Box
          role={editable ? 'button' : undefined}
          tabIndex={editable ? 0 : undefined}
          aria-label={editable ? `Artikelnummer: ${number || 'tom'}` : undefined}
          title={origin}
          onClick={start}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === 'F2') {
              event.preventDefault();
              start();
            }
          }}
          sx={{
            ...cellTextSx,
            flex: 1,
            minHeight: 20,
            px: 0.75,
            mx: -0.75,
            borderRadius: '4px',
            color: launchColors.textSecondary,
            opacity: saving ? 0.6 : 1,
            cursor: editable ? 'text' : 'default',
            '&:hover': editable ? { bgcolor: launchColors.softNavy } : undefined,
            '&:focus-visible': { outline: `2px solid ${launchColors.navy}`, outlineOffset: 0 },
          }}
        >
          {number}
        </Box>
      )}
      {catalog && canOpenCatalog && (
        <Chip
          size="small"
          label={fromAlcohol ? 'Alkohol' : 'Produktinfo'}
          title={`Öppna i ${catalogName}`}
          aria-label={`Öppna ${row.articleNumber} i ${catalogName}`}
          onClick={() => (fromAlcohol ? openAlcoholInfo(catalog.productId) : openProductInfo(catalog.productId))}
          sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: launchColors.petrolLight, color: launchColors.petrolDark }}
        />
      )}
      {ambiguous && (
        <Chip
          size="small"
          label={`${ambiguous.options.length} träffar`}
          onClick={(event) => setMenu(event.currentTarget)}
          sx={{ height: 20, fontSize: 11, bgcolor: launchColors.warningBg, color: launchColors.warningText }}
        />
      )}
      {editable && linked && (
        <IconButton
          size="small"
          aria-label="Ändra artikelnummer"
          onClick={start}
          sx={{ p: 0.25, opacity: 0, '.MuiTableRow-root:hover &, &:focus-visible': { opacity: 1 } }}
        >
          <EditOutlinedIcon sx={{ fontSize: 14 }} />
        </IconButton>
      )}
      {ambiguous && (
        <Menu anchorEl={menu} open={Boolean(menu)} onClose={() => setMenu(null)}>
          <Typography sx={{ px: 2, py: 0.5, fontSize: 12, color: launchColors.textSecondary }}>
            {editable ? 'Välj rätt produkt' : 'Öppna i Athena'}
          </Typography>
          {ambiguous.options.map((option) => (
            <MenuItem
              key={option.assetId}
              onClick={() => {
                setMenu(null);
                if (editable) {
                  const title = option.title.trim();
                  void save({
                    articleNumber: row.articleNumber,
                    assetId: option.assetId,
                    ...(title ? { articleName: title } : {}),
                  });
                }
                else openAssetInAthena(option.assetId);
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, maxWidth: 320 }}>
                <Thumb option={option} />
                <OptionText option={option} />
              </Stack>
            </MenuItem>
          ))}
        </Menu>
      )}
    </Stack>
  );
};

export default ArticleNumberCell;
